/**
 * LeadRouterDealerPage.tsx — M6 Dealer Management (web), Lead Router v2 DEALER
 * layer (Squad S8, AI-egoj, 2026-07-22). Manager view. Built to mockup M6.
 *
 *  • per-manager dealers (grouped), each with product-group sub-cards
 *  • pincode served-areas editor (Add-dealer modal)
 *  • product-group multiselect
 *  • AUTO toggle (semi-auto default) per manager (ManagerRoutingConfig)
 *  • dealer-primary marking per (dealer, product group) — batched over pincodes
 *
 * 🔒 Rep ALWAYS owns — a dealer is only ASSIGNED. Every banner here reinforces it.
 * All colour via var(--…) tokens (no `primary` Tailwind), matching LeadRouterPage.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Store, ShieldCheck, Info, Loader2 } from 'lucide-react';
import { api } from '@/utils/api';
import {
  leadRouterDealerApi,
  duplicateProductGroup,
  dealerPincodes,
  parsePincodeInput,
  pincodesToServedAreas,
  primaryLabel,
  autoLabel,
  type RouterDealerV2,
} from '@/api/leadRouterDealer';

// UI label map for the SS routing groups (mockup M6). The stored KEY is what the
// resolver uses; this is only a display label. Options are the union of these +
// whatever groups the loaded dealers already carry (so it's not hardcoded-only).
const GROUP_LABELS: Record<string, string> = {
  SS_TANKS: 'SS Tanks',
  SS_PLUMBING: 'SS Plumbing',
  SWH: 'SWH',
  DOMESTIC_HP: 'Domestic HP',
  COMMERCIAL_HP: 'Commercial HP',
  SOLAR_SPACE_HEATING: 'Solar Space Heating',
  COMMERCIAL_HWT: 'Commercial HWT',
  HEADERS: 'Headers',
};
const DEFAULT_GROUPS = Object.keys(GROUP_LABELS);
const groupLabel = (k: string): string => GROUP_LABELS[k] ?? k;

interface StaffLite { id: string; name: string }

export default function LeadRouterDealerPage() {
  const [dealers, setDealers] = useState<RouterDealerV2[]>([]);
  const [staff, setStaff] = useState<StaffLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [d, users] = await Promise.all([
        leadRouterDealerApi.list(),
        api
          .get<{ data?: Array<{ id: string; firstName?: string; lastName?: string; email?: string }> }>('/auth/users')
          .then((r) => r.data?.data ?? [])
          .catch(() => []),
      ]);
      setDealers(d);
      setStaff(
        users.map((u) => ({
          id: u.id,
          name: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || u.id,
        })),
      );
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Failed to load dealers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const nameOf = useCallback(
    (id: string | null) => (id ? staff.find((s) => s.id === id)?.name ?? 'Manager' : 'Unassigned'),
    [staff],
  );

  // Group dealers by managing manager.
  const byManager = useMemo(() => {
    const m = new Map<string, RouterDealerV2[]>();
    for (const d of dealers) {
      const key = d.managerUserId ?? '__none__';
      (m.get(key) ?? m.set(key, []).get(key)!).push(d);
    }
    return Array.from(m.entries());
  }, [dealers]);

  const setAuto = useCallback(
    async (managerId: string, on: boolean) => {
      setBusy(`auto:${managerId}`);
      try {
        await leadRouterDealerApi.setAutoRouting(managerId, on);
        await load();
      } catch (e: unknown) {
        setErr(e instanceof Error ? e.message : 'Toggle failed');
      } finally {
        setBusy(null);
      }
    },
    [load],
  );

  // Batch-toggle dealer-primary for a (dealer, group) across its declared pincodes.
  const toggleDealerPrimary = useCallback(
    async (dealer: RouterDealerV2, groupKey: string, makePrimary: boolean) => {
      const pins = dealerPincodes(dealer.servedAreas);
      const targets = pins.length ? pins : ['*'];
      setBusy(`primary:${dealer.id}:${groupKey}`);
      try {
        for (const pin of targets) {
          const body = {
            dealerId: dealer.id,
            productGroupKey: groupKey,
            matchType: (pin === '*' ? 'catchall' : 'pincode') as 'pincode' | 'catchall',
            matchValue: pin,
          };
          if (makePrimary) await leadRouterDealerApi.markDealerPrimary(body);
          else await leadRouterDealerApi.unmarkDealerPrimary(body);
        }
        await load();
      } catch (e: unknown) {
        setErr(e instanceof Error ? e.message : 'Dealer-primary toggle failed');
      } finally {
        setBusy(null);
      }
    },
    [load],
  );

  return (
    <div style={{ padding: 20, maxWidth: 1080, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--ink-3)' }}>
            Layer 3 · Dealers (second layer) · Manager view
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--ink-0)', margin: '2px 0 0' }}>
            Dealer Management
          </h1>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          style={btnPrimary}
        >
          <Plus size={15} /> Add dealer
        </button>
      </div>

      {/* Rep-always-owns banner */}
      <div style={bannerBox}>
        <ShieldCheck size={16} style={{ color: 'var(--forest-600)', flexShrink: 0, marginTop: 1 }} />
        <div>
          <b>Rep always owns</b> — a dealer is only <i>assigned</i>. Dispatching to a dealer creates an
          assignment (the dealer works / quotes it); the rep stays owner-of-record the entire time. A dealer
          never becomes the owner. <b>Hard rule:</b> a dealer can't have the same product twice. Dealer
          territory is mapped by <b>pincodes</b>.
        </div>
      </div>

      {err && (
        <div style={{ ...bannerBox, background: 'var(--danger-bg)', border: '1px solid #EFCFCA', color: 'var(--danger)' }}>
          {err}
          <button type="button" onClick={() => void load()} style={{ marginLeft: 'auto', textDecoration: 'underline' }}>
            Retry
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--ink-3)', padding: 24 }}>
          <Loader2 size={16} className="spin" /> Loading dealers…
        </div>
      ) : dealers.length === 0 ? (
        <div style={{ color: 'var(--ink-3)', padding: 24 }}>
          No dealers yet. Use <b>Add dealer</b> to appoint one under a manager.
        </div>
      ) : (
        byManager.map(([managerId, list]) => {
          const auto = list.some((d) => d.autoDealerRouting);
          const realMgr = managerId !== '__none__';
          return (
            <div key={managerId} style={{ marginTop: 18 }}>
              {/* Manager band + AUTO toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ fontWeight: 600, color: 'var(--ink-1)' }}>
                  Manager: {realMgr ? nameOf(managerId) : 'Unassigned'}
                </div>
                {realMgr && (
                  <button
                    type="button"
                    disabled={busy === `auto:${managerId}`}
                    onClick={() => void setAuto(managerId, !auto)}
                    style={{ ...pill, background: auto ? 'var(--saffron-50)' : 'var(--ivory-2)', color: auto ? 'var(--saffron-600)' : 'var(--ink-2)', marginLeft: 'auto' }}
                    title="Semi-auto (default) creates a hand-off task; AUTO removes the human gate for this manager's dealers."
                  >
                    {autoLabel(auto)}
                  </button>
                )}
              </div>

              {list.map((d) => (
                <DealerCard
                  key={d.id}
                  dealer={d}
                  busy={busy}
                  onToggleDealerPrimary={toggleDealerPrimary}
                />
              ))}
            </div>
          );
        })
      )}

      {showAdd && (
        <AddDealerModal
          staff={staff}
          existingGroups={Array.from(new Set([...DEFAULT_GROUPS, ...dealers.flatMap((d) => d.productGroups)]))}
          onClose={() => setShowAdd(false)}
          onSaved={async () => {
            setShowAdd(false);
            await load();
          }}
        />
      )}
    </div>
  );
}

// ─── Dealer card ──────────────────────────────────────────────────────────────

function DealerCard({
  dealer,
  busy,
  onToggleDealerPrimary,
}: {
  dealer: RouterDealerV2;
  busy: string | null;
  onToggleDealerPrimary: (d: RouterDealerV2, groupKey: string, makePrimary: boolean) => void;
}) {
  const pins = dealerPincodes(dealer.servedAreas);
  const initials = dealer.name.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={avatar}>{initials || <Store size={14} />}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: 'var(--ink-0)' }}>{dealer.name}</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            {dealer.productGroups.map(groupLabel).join(' · ') || 'No products yet'}
            {dealer.phone ? ` · ${dealer.phone}` : ''}
          </div>
        </div>
      </div>

      {/* Per product-group sub-cards */}
      {dealer.productGroups.map((g) => {
        // Dealer-primary if EVERY declared pincode (or catchall) has an active
        // dealer-primary row (matched by matchType+matchValue on this dealer).
        const targets = pins.length ? pins : ['*'];
        const dp = targets.every((pin) => {
          const mt = pin === '*' ? 'catchall' : 'pincode';
          const mv = pin === '*' ? '*' : pin;
          return dealer.dealerPrimary.some((r) => r.matchType === mt && r.matchValue === mv);
        });
        const isBusy = busy === `primary:${dealer.id}:${g}`;
        return (
          <div key={g} style={subCard}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontWeight: 600, color: 'var(--ink-1)', fontSize: 13 }}>{groupLabel(g)}</div>
              <button
                type="button"
                disabled={isBusy}
                onClick={() => onToggleDealerPrimary(dealer, g, !dp)}
                style={{ ...pill, marginLeft: 'auto', background: dp ? 'var(--forest-50)' : 'var(--ivory-2)', color: dp ? 'var(--forest-600)' : 'var(--ink-2)' }}
                title={dp ? 'Dealer wins for this product+area. Click to give the rep priority again.' : 'Rep wins by default. Click to make this dealer primary for this product+area.'}
              >
                {isBusy ? '…' : primaryLabel(dp)}
              </button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {pins.length ? (
                pins.map((p) => (
                  <span key={p} style={chip}>
                    {p}
                  </span>
                ))
              ) : (
                <span style={{ ...chip, background: 'var(--saffron-50)', color: 'var(--saffron-600)' }}>
                  No pincodes mapped — add pincodes to scope this dealer
                </span>
              )}
            </div>
            {dp && (
              <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 6 }}>
                When dealer-primary, the rep/manager is still <b>notified</b> of every lead dispatched to this dealer.
              </div>
            )}
          </div>
        );
      })}

      <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 8, display: 'flex', gap: 6 }}>
        <Info size={13} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>
          Semi-auto is the safe default: an outbound hand-off task lands in the manager's queue; nothing leaves
          until a human confirms. AUTO removes the gate for this manager's dealers — granular, reversible, audited.
        </span>
      </div>
    </div>
  );
}

// ─── Add-dealer modal ─────────────────────────────────────────────────────────

function AddDealerModal({
  staff,
  existingGroups,
  onClose,
  onSaved,
}: {
  staff: StaffLite[];
  existingGroups: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [managerUserId, setManagerUserId] = useState<string>('');
  const [groups, setGroups] = useState<string[]>([]);
  const [pincodeText, setPincodeText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dup = duplicateProductGroup(groups);
  const canSave = name.trim().length > 0 && !dup && !saving;

  const toggleGroup = (g: string) =>
    setGroups((prev) => (prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]));

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await leadRouterDealerApi.create({
        name: name.trim(),
        phone: phone.trim() || null,
        managerUserId: managerUserId || null,
        productGroups: groups,
        servedAreas: pincodesToServedAreas(parsePincodeInput(pincodeText)),
      });
      onSaved();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
      setSaving(false);
    }
  };

  return (
    <div style={mask} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modal}>
        <h2 style={{ fontSize: 17, fontWeight: 700, color: 'var(--ink-0)', margin: '0 0 12px' }}>Add dealer</h2>

        <label style={lbl}>Dealer name</label>
        <input style={inp} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sharma Traders" />

        <label style={lbl}>WhatsApp phone (for M9 dispatch)</label>
        <input style={inp} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 …" />

        <label style={lbl}>Managing manager</label>
        <select style={inp} value={managerUserId} onChange={(e) => setManagerUserId(e.target.value)}>
          <option value="">— you (default) —</option>
          {staff.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>

        <label style={lbl}>Products (a dealer can't have the same product twice)</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 4 }}>
          {existingGroups.map((g) => {
            const on = groups.includes(g);
            return (
              <button
                key={g}
                type="button"
                onClick={() => toggleGroup(g)}
                style={{ ...chip, cursor: 'pointer', background: on ? 'var(--forest-50)' : 'var(--ivory-2)', color: on ? 'var(--forest-600)' : 'var(--ink-2)', border: on ? '1px solid var(--forest-100)' : '1px solid var(--line)' }}
              >
                {groupLabel(g)}
              </button>
            );
          })}
        </div>
        {dup && <div style={{ color: 'var(--danger)', fontSize: 12 }}>Duplicate product "{dup}".</div>}

        <label style={lbl}>Served pincodes (comma/space separated)</label>
        <textarea
          style={{ ...inp, minHeight: 60, resize: 'vertical' }}
          value={pincodeText}
          onChange={(e) => setPincodeText(e.target.value)}
          placeholder="132103, 132140, 132001"
        />

        {error && <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 6 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={btnGhost}>
            Cancel
          </button>
          <button type="button" disabled={!canSave} onClick={() => void save()} style={{ ...btnPrimary, opacity: canSave ? 1 : 0.5 }}>
            {saving ? 'Saving…' : 'Add dealer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── inline styles (var(--…) tokens) ──────────────────────────────────────────

const btnPrimary: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10,
  background: 'var(--forest)', color: 'var(--ivory-0)', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer',
};
const btnGhost: React.CSSProperties = {
  padding: '8px 14px', borderRadius: 10, background: 'var(--ivory-0)', color: 'var(--ink-1)',
  border: '1px solid var(--line)', fontWeight: 600, fontSize: 13, cursor: 'pointer',
};
const bannerBox: React.CSSProperties = {
  display: 'flex', gap: 10, alignItems: 'flex-start', padding: 12, borderRadius: 12, marginTop: 8,
  background: 'var(--forest-50)', border: '1px solid var(--forest-100)', color: 'var(--ink-1)', fontSize: 12.5,
};
const card: React.CSSProperties = {
  padding: 14, borderRadius: 14, background: 'var(--ivory-0)', border: '1px solid var(--line)', marginBottom: 10,
};
const subCard: React.CSSProperties = {
  padding: 10, borderRadius: 10, background: 'var(--ivory-2)', border: '1px solid var(--line)', marginTop: 10,
};
const avatar: React.CSSProperties = {
  width: 34, height: 34, borderRadius: 9, background: 'var(--ink-0)', color: 'var(--ivory-0)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12,
};
const pill: React.CSSProperties = {
  padding: '4px 10px', borderRadius: 999, border: 'none', fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
};
const chip: React.CSSProperties = {
  padding: '3px 9px', borderRadius: 999, background: 'var(--ivory-2)', border: '1px solid var(--line)',
  color: 'var(--ink-1)', fontSize: 11.5, fontWeight: 500,
};
const mask: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center',
  justifyContent: 'center', zIndex: 50, padding: 16,
};
const modal: React.CSSProperties = {
  width: 440, maxWidth: '100%', maxHeight: '90vh', overflow: 'auto', background: 'var(--ivory-0)',
  borderRadius: 16, border: '1px solid var(--line)', padding: 18, boxShadow: 'var(--sh-4)',
};
const lbl: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', margin: '10px 0 4px',
};
const inp: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: 9, border: '1px solid var(--line)',
  background: 'var(--ivory-0)', color: 'var(--ink-0)', fontSize: 13, boxSizing: 'border-box',
};
